import { useState } from 'react';
import { useCarriers, useTrunks } from '../../lib/api/hooks-billing.js';
import { StatusBadge, TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { useSession } from '../../lib/auth/session.js';
import { api } from '../../lib/api/client.js';
import { TrunkEditor } from './TrunkEditor.js';

export function CarriersPage() {
  const can = useSession((s) => s.can);
  const [selected, setSelected] = useState<string | undefined>(undefined);
  const carriers = useCarriers();
  const trunks = useTrunks(selected);

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Carriers &amp; trunks</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            Upstream SIP connectivity for inbound and outbound calls
          </p>
        </div>
      </div>

      {carriers.error && <ErrorBanner error={carriers.error} />}

      <div className="arc-grid" style={{ gridTemplateColumns: 'minmax(240px, 300px) 1fr', alignItems: 'start' }}>
        <div className="arc-card">
          <div className="arc-card-head"><h2 className="arc-h2">Carriers</h2></div>
          <div>
            <button
              type="button"
              className="arc-nav-item"
              style={{ width: '100%', border: 'none', background: !selected ? 'var(--arc-accent-subtle)' : 'none', color: 'var(--arc-fg)', borderRadius: 0, padding: '10px 14px' }}
              onClick={() => setSelected(undefined)}
            >
              All carriers
            </button>
            {carriers.data?.data.map((c) => (
              <button
                key={c.id}
                type="button"
                className="arc-nav-item"
                style={{ width: '100%', border: 'none', background: selected === c.id ? 'var(--arc-accent-subtle)' : 'none', color: 'var(--arc-fg)', borderRadius: 0, padding: '10px 14px' }}
                onClick={() => setSelected(c.id)}
              >
                <div className="arc-stack" style={{ minWidth: 0, flex: 1 }}>
                  <span style={{ fontWeight: 550 }}>{c.name}</span>
                  <span className="arc-subtle" style={{ fontSize: 11.5 }}>
                    {c.trunk_count ?? 0} trunk{c.trunk_count === 1 ? '' : 's'}
                  </span>
                </div>
                <StatusBadge status={c.status} />
              </button>
            ))}
            {!carriers.isLoading && carriers.data?.data.length === 0 && (
              <Empty icon="carrier" title="No carriers" hint="Add a carrier to configure trunks." />
            )}
          </div>
        </div>

        <div className="arc-card">
          <div className="arc-card-head">
            <h2 className="arc-h2">Trunks</h2>
            <span className="arc-subtle arc-num" style={{ fontSize: 12.5 }}>
              {trunks.data ? `${trunks.data.data.length}` : ''}
            </span>
          </div>

          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead>
                <tr>
                  <th>Name</th><th>Host</th><th>Direction</th><th>Auth</th>
                  <th>Health</th><th className="arc-td-right">Capacity</th><th />
                </tr>
              </thead>
              {trunks.isLoading ? <TableSkeleton cols={7} /> : (
                <tbody>
                  {trunks.data?.data.map((t) => (
                    <TrunkRow key={t.id} trunk={t} canTest={can('trunk:read')} />
                  ))}
                </tbody>
              )}
            </table>
          </div>

          {!trunks.isLoading && trunks.data?.data.length === 0 && (
            <Empty
              icon="carrier"
              title="No trunks configured"
              hint="Without a trunk there is no route to the PSTN — on-net calls work, external calls do not."
            />
          )}
        </div>
      </div>
    </>
  );
}

function TrunkRow({ trunk, canTest }: { trunk: import('@arcnet/contracts').Trunk; canTest: boolean }) {
  const [testing, setTesting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [results, setResults] = useState<Array<{ input: string; output: string; note: string | null }> | null>(null);

  const runTest = async () => {
    setTesting(true);
    try {
      const r = await api.post<{ results: Array<{ input: string; output: string; note: string | null }> }>(
        `/v1/trunks/${trunk.id}/actions/test-number-format`,
        { numbers: ['+27821234567', '+27115550100', '+441632960961'], direction: 'outbound' },
      );
      setResults(r.results);
    } finally { setTesting(false); }
  };

  return (
    <>
      <tr>
        <td>
          <div className="arc-stack">
            <span style={{ fontWeight: 550 }}>{trunk.name}</span>
            <span className="arc-subtle" style={{ fontSize: 11.5 }}>{trunk.carrier_name}</span>
          </div>
        </td>
        <td className="arc-mono arc-subtle">{trunk.host}:{trunk.port}/{trunk.transport}</td>
        <td><Badge tone="muted">{trunk.direction}</Badge></td>
        <td>
          <div className="arc-stack" style={{ fontSize: 12.5 }}>
            <span>{trunk.auth_mode === 'ip' ? 'IP ACL' : 'Register'}</span>
            <span className="arc-subtle">
              {trunk.auth_mode === 'ip'
                ? `${trunk.source_ips.length} source${trunk.source_ips.length === 1 ? '' : 's'}`
                : trunk.auth_secret_set ? 'secret set' : 'no secret'}
            </span>
          </div>
        </td>
        <td><StatusBadge status={trunk.health_status === 'up' ? 'active' : trunk.health_status} /></td>
        <td className="arc-td-right arc-num">
          {trunk.max_concurrent_calls ?? '∞'}
          <span className="arc-subtle"> calls</span>
        </td>
        <td className="arc-td-right">
          <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
            {canTest && (
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={runTest} disabled={testing}>
                {testing ? '…' : 'Test format'}
              </button>
            )}
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
              onClick={() => setEditing((v) => !v)}>
              {editing ? 'Close' : 'Configure'}
            </button>
          </div>
        </td>
      </tr>
      {editing && (
        <tr>
          {/* Full width: the editor is six groups of settings and squeezing it
              into a column would be worse than the dialog it replaces. */}
          <td colSpan={7} style={{ padding: 0 }}>
            <TrunkEditor trunk={trunk} onDone={() => setEditing(false)} />
          </td>
        </tr>
      )}
      {results && (
        <tr>
          <td colSpan={7} style={{ background: 'var(--arc-surface-2)' }}>
            <div style={{ padding: '4px 0' }}>
              <div className="arc-subtle" style={{ fontSize: 12, marginBottom: 6 }}>
                Outbound transformation — what this carrier will receive:
              </div>
              {results.map((r) => (
                <div key={r.input} className="arc-mono" style={{ fontSize: 12.5, marginBottom: 2 }}>
                  {r.input} <span className="arc-subtle">→</span> <strong>{r.output}</strong>
                  {r.note && <span className="arc-subtle"> ({r.note})</span>}
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
